import {
  ExperienceForm,
  ExperienceFormSchema,
  ExperienceSearchParams,
} from '@reward-platform/ancillaries-schemas/experience'
import { addDays } from 'date-fns/addDays'
import { format } from 'date-fns/format'
import { useRouter } from 'next/router'
import { stringify } from 'qs'
import { ForwardedRef, useCallback, useMemo, useState } from 'react'
import { parseDestinationFromUrl } from '~/utils/locationComparison'
import { useLocationChanged } from '~/hooks/useLocationChanged'
import { useForm, UseFormReturn } from 'react-hook-form'
import { ZodIssueCode } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useDebounceValue } from 'usehooks-ts'
import { useFormValidationMessages } from '~/hooks/useMessages'
import { useTranslations } from 'next-intl'
import { useAncillariesLocationsSearch } from '~/hooks/useAncillariesLocations'
import { usePlatformConfig, usePlatformRoutes } from '~/context/platform'
import useFilterRouter from '~/context/filters/useFilterRouter'

/**
 * Set up and manage state for the Destination autocomplete field
 */
export function useDestinationState(
  form: UseFormReturn<ExperienceForm>,
  defaultState: ExperienceForm['destination']
) {
  const fieldName: keyof ExperienceSearchParams = 'destination'
  const [searchString, setSearchString] = useState<string>(defaultState?.fullName || '')
  const fieldValue = form.getValues(fieldName)

  const [debouncedSearch] = useDebounceValue(searchString, 500)

  const querySameAsFullName = searchString === fieldValue.fullName
  const canSearch =
    debouncedSearch !== '' && debouncedSearch === searchString && !querySameAsFullName

  const { data: locations, isLoading } = useAncillariesLocationsSearch(
    'EXPERIENCE',
    { searchString: debouncedSearch },
    { isEnabled: canSearch }
  )

  const noResultsFound = locations?.length === 0 && !isLoading && canSearch

  const onInputFocus = useCallback(
    // Use shorter name as search query when re-focusing input field, as fullName will usually not yield results. Could be removed if search is enhanced in future.
    (e: { target: { select: () => void } }, openMenu: () => void) => {
      e.target.select()
      setSearchString(fieldValue.fullName)
      openMenu()
    },
    [fieldValue.fullName]
  )

  const handleAutocompleteChange = useCallback(
    (value: string) => {
      setSearchString(value)
      if (value.length === 0) {
        form.setValue(fieldName, { id: '', type: '', fullName: '' }, { shouldDirty: true })
      }
    },
    [form]
  )

  const handleSuggestionClick = useCallback(
    (key: string) => {
      const location = locations?.find(({ id }) => id === key)
      if (location) {
        setSearchString(location.fullName)
        form.clearErrors(fieldName)
        form.setValue(fieldName, location, { shouldValidate: true })
      }
    },
    [locations, form, fieldName]
  )

  return {
    isLoading,
    locations: isLoading || !canSearch || !locations ? [] : locations,
    ...fieldValue,
    searchString,
    noResultsFound,
    onInputFocus,
    handleAutocompleteChange,
    handleSuggestionClick,
  }
}

/**
 * Set up and manage the state for the date range fields
 */
export function useDateRangeState(
  form: UseFormReturn<ExperienceForm>,
  [defaultStartDate, defaultEndDate]: [Date, Date | null]
) {
  const [startDate, setStartDate] = useState(defaultStartDate)
  const [endDate, setEndDate] = useState<Date | null>(defaultEndDate)

  const handleDateChange = useCallback(
    (dates: [Date | null, Date | null]) => {
      if (!dates || !Array.isArray(dates)) {
        return
      }
      const [start, end] = dates
      if (!start) {
        return
      }

      setStartDate(start)
      form.setValue('startDateTime', start)

      if (end?.getTime() === start.getTime()) {
        end.setDate(start.getDate() + 1)
      }

      if (end === null) {
        form.resetField('endDateTime')
        form.setValue('endDateTime', null)
      }

      setEndDate(end)
      if (end) {
        form.setValue('endDateTime', end, { shouldValidate: true })
      }
    },
    [form]
  )

  const minDate = useMemo(() => addDays(new Date(), 3), [])
  const maxDate = useMemo(() => addDays(startDate, 365), [startDate])

  return {
    startDate,
    endDate,
    minDate,
    maxDate,
    handleDateChange,
  }
}

/**
 * Automatically converts the zod validation errors to a translated human readable error message
 */
function useExperienceSearchErrors(errors: UseFormReturn<ExperienceForm>['formState']['errors']) {
  const { notAValid, mustBeSelected, getValidationErrorMessage } = useFormValidationMessages()

  const t = useTranslations()

  const errorMap = useMemo(
    () => ({
      searchString: { [ZodIssueCode.custom]: notAValid(t('common.forms.labels.search')) },
      startDateTime: { [ZodIssueCode.custom]: notAValid(t('common.forms.labels.start_date')) },
      endDateTime: { [ZodIssueCode.invalid_type]: notAValid(t('common.forms.labels.end_date')) },
      destination: { [ZodIssueCode.custom]: mustBeSelected(t('common.forms.labels.destination')) },
    }),
    [t, notAValid, mustBeSelected]
  )

  // Returns standard error regardless of specific nested errors
  const getDestinationError = useCallback(
    () => errors?.destination && errorMap.destination[ZodIssueCode.custom],
    [errors, errorMap]
  )

  const getFieldError = useCallback(
    (field: keyof typeof errorMap) =>
      errors?.[field] &&
      getValidationErrorMessage({
        errorMap,
        errors,
        field,
      }),
    [errors, getValidationErrorMessage, errorMap]
  )

  return {
    getFieldError,
    getDestinationError,
  }
}

/**
 * Sets up the react-hook-form and creates the submit handler and error translations
 * @param defaultValues The default values for the form state
 */
export function useExperienceSearchForm(
  defaultValues: ExperienceForm,
  toggleRef?: ForwardedRef<{ press: () => void }>
) {
  const router = useRouter()
  const { query: filtersQuery } = useFilterRouter('EXPERIENCE')
  const { createRoutePath } = usePlatformRoutes()
  const { opCo } = usePlatformConfig()
  const form = useForm<ExperienceForm>({
    defaultValues,
    resolver: zodResolver(ExperienceFormSchema),
  })
  const { getValues } = form
  const errors = useExperienceSearchErrors(form.formState.errors)

  // Reset filters if destination changed; preserve if only dates changed
  const hasDestinationChanged = useLocationChanged(parseDestinationFromUrl)

  const handleFormSubmit = useCallback(() => {
    const { startDateTime, endDateTime, destination } = getValues()

    if (!endDateTime) {
      form.setError('endDateTime', { type: ZodIssueCode.invalid_type })
      return
    }

    const destinationChanged = hasDestinationChanged({ id: destination.id, type: destination.type })

    const searchData = {
      destination: {
        type: destination.type,
        id: destination.id,
        fullName: destination.fullName,
      },
      dates: {
        startDateTime: format(new Date(startDateTime), "y-MM-dd'T'HH:mm:ss"),
        endDateTime: endDateTime && format(new Date(endDateTime), "y-MM-dd'T'HH:mm:ss"),
      },
      page: 1, // this is always a new search
      opco: opCo,
      // Only inherit filters if destination didn't change (dates changed only)
      ...(destinationChanged ? {} : filtersQuery ?? {}),
    }

    if (toggleRef && typeof toggleRef !== 'function') {
      toggleRef?.current?.press()
    }

    router.push({
      pathname: createRoutePath('/experiences/results'),
      query: stringify(searchData),
    })
  }, [
    createRoutePath,
    form,
    getValues,
    router,
    toggleRef,
    filtersQuery,
    hasDestinationChanged,
    opCo,
  ])

  return {
    form,
    handleFormSubmit,
    errors,
  }
}
